import type { HTMLAttributes, ReactElement } from "react";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import styles from "./ValueUnavailable.module.css";

export interface ValueUnavailableProps extends HTMLAttributes<HTMLSpanElement> {
  /** On-screen wording. Keep it a WORD, never a dash or a zero. */
  label?: string;
  /**
   * Why the value is absent, in plain language — required on purpose. The
   * component will not let a caller render an unexplained blank, because an
   * unexplained blank is what invites the reader to assume "zero".
   */
  reason: string;
}

/**
 * The honest-absence primitive (ADR-052 §3, ISSUE-329 AC-3).
 *
 * A missing value in this product is *meaningful* — a Telegram user has no
 * registration date because there was never a registration event, and
 * `profileCount` is unknown for everyone until the profile model lands. Both
 * are easy to misread as "zero" or "never active" if rendered as `0`, as an
 * em-dash, or as an empty cell.
 *
 * So absence gets a word, a distinct muted-italic treatment that no real value
 * uses, and a screen-reader-announced reason. Never substitute a dash.
 */
export function ValueUnavailable({
  label = "Not available",
  reason,
  className,
  ...rest
}: ValueUnavailableProps): ReactElement {
  const classes = [styles.unavailable, className].filter(Boolean).join(" ");
  return (
    <span className={classes} title={reason} {...rest}>
      {label}
      <VisuallyHidden> — {reason}</VisuallyHidden>
    </span>
  );
}
